import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import breakpoint from 'styled-components-breakpoint';
import axios from 'axios';
import { InView } from 'react-intersection-observer';
import CountUp from 'react-countup';

import { hasWindow } from '../utils';
import People from '../assets/images/people.svg';
import Sessions from '../assets/images/tablet.svg';
import Factories from '../assets/images/factory.svg';

import Grid from './Grid';
import Cell from './Cell';
import Block from './Block';
import H2 from './H2';
import H3 from './H3';
import H4 from './H4';

const StatIcon = styled.img`
  height: 40px;
  margin-bottom: ${({ theme }) => theme.spacing.baseUnit};

  ${breakpoint('smallTablet')`
    height: 60px;
  `};
`;

class PageStatistics extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      hasError: false,
      stats: {
        users: 0,
        sessions: 0,
        factories: 0,
      },
    };
  }

  componentDidMount() {
    this.fetchStats();
  }

  fetchStats = async () => {
    if (hasWindow()) {
      const stats = JSON.parse(localStorage.getItem('quizrr-stats'));
      const sessionHasData = JSON.parse(
        sessionStorage.getItem('session-has-stats'),
      );

      this.setState({
        stats: { ...stats },
      });

      if (!sessionHasData) {
        this.setState({
          isLoading: !stats,
        });

        try {
          const { data } = await axios.get(
            `${process.env.GATSBY_API_URL}/api/total_stats`,
          );

          localStorage.setItem('quizrr-stats', JSON.stringify(data));
          sessionStorage.setItem('session-has-stats', true);

          this.setState({
            stats: data,
            isLoading: false,
          });
        } catch (err) {
          this.setState({
            isLoading: false,
            hasError: true,
          });
        }
      }
    }
  };

  render() {
    const { content } = this.props;
    const { stats, isLoading, hasError } = this.state;

    const statsItems = [
      {
        title: content.people_trained_label.text,
        icon: People,
        value: stats.users,
      },
      {
        title: content.training_sessions_label.text,
        icon: Sessions,
        value: stats.sessions,
      },
      {
        title: content.factories_label.text,
        icon: Factories,
        value: stats.factories,
      },
    ];

    return hasError ? null : (
      <Block
        id={content.internal_link_id}
        backgroundColor="grayLight"
        py={{ mobile: 'baseUp4', tablet: 'baseUp12' }}
      >
        {content.title1 ? (
          <H2
            textAlign="center"
            mb={{ mobile: 'baseUnit', tablet: 'baseUp10' }}
            color="coral"
          >
            {content.title1.text}
          </H2>
        ) : null}
        <Grid>
          {statsItems.map(stat => (
            <Cell
              key={`stat-item-${stat.title}-${stat.value}`}
              px="baseUnit"
              py={{ mobile: 'baseUp4', smallTablet: '0' }}
              size={{ mobile: 1, smallTablet: 1 / 3 }}
            >
              <Block px="baseUnit" position="relative">
                <StatIcon src={stat.icon} alt={stat.title} />
                <InView as="span" triggerOnce>
                  {({ inView, ref }) => (
                    <H3 ref={ref} color="coral" textAlign="center">
                      {isLoading || !inView ? (
                        '...'
                      ) : (
                        <CountUp
                          useEasing
                          /* eslint-disable */
                          easingFn={(t, b, c, d) =>
                            c * Math.sqrt(1 - (t = t / d - 1) * t) + b
                          }
                          /* eslint-enable */
                          end={stat.value}
                          duration={4}
                          separator=" "
                        />
                      )}
                    </H3>
                  )}
                </InView>
                <H4 color="blueDark" textAlign="center">
                  {stat.title}
                </H4>
              </Block>
            </Cell>
          ))}
        </Grid>
      </Block>
    );
  }
}

PageStatistics.propTypes = {
  content: PropTypes.shape({
    internal_link_id: PropTypes.string,
    title1: PropTypes.shape({ text: PropTypes.string }),
    factories_label: PropTypes.shape({ text: PropTypes.string }),
    people_trained_label: PropTypes.shape({ text: PropTypes.string }),
    training_sessions_label: PropTypes.shape({ text: PropTypes.string }),
  }).isRequired,
};

export default PageStatistics;

export const query = graphql`
  fragment PageStatisticsFragment on PrismicPageBodyStatistics {
    primary {
      internal_link_id
      title1 {
        text
      }
      factories_label {
        text
      }
      people_trained_label {
        text
      }
      training_sessions_label {
        text
      }
    }
    slice_type
    id
  }
`;
